import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../apiFetch';

/**
 * Custom hook for admin API calls.
 * Automatically includes X-Admin-Token header and handles 2FA expiration.
 *
 * Usage:
 *   const adminFetch = useAdminFetch();
 *   const data = await adminFetch('/api/admin/some-endpoint/');
 */
export const useAdminFetch = () => {
  const navigate = useNavigate();

  const adminFetch = useCallback(async (
    url: string,
    options: RequestInit = {}
  ) => {
    const adminToken = sessionStorage.getItem('admin_token');

    // No token - redirect to admin for 2FA
    if (!adminToken) {
      navigate('/admin');
      throw new Error('Admin authentication required');
    }

    // Merge headers with admin token
    const headers = {
      ...options.headers,
      'X-Admin-Token': adminToken,
    };

    try {
      const data = await apiFetch(url, { ...options, headers });

      // Check if response requires 2FA
      if (data?.requires_admin_2fa) {
        sessionStorage.removeItem('admin_token');
        navigate('/admin');
        throw new Error('Admin session expired');
      }

      return data;
    } catch (error: any) {
      // Handle 2FA/session expiration errors
      if (
        error.message?.includes('Admin 2FA') ||
        error.message?.includes('session expired') ||
        error.message?.includes('Admin authentication required')
      ) {
        sessionStorage.removeItem('admin_token');
        navigate('/admin');
      }
      throw error;
    }
  }, [navigate]);

  return adminFetch;
};

export default useAdminFetch;
